'use client';

import { featureFlags } from '@lib/constants/network-constants';
import { SearchService } from '@lib/constants/variants';
import { getFeatureFlagState } from '@lib/data';
import { usePathname, useSearchParams } from 'next/navigation';
import React, { useEffect, useState, ReactNode, useContext, useMemo } from 'react';
import { getLogger } from '@lib/util/logging-util';
interface SearchServiceType {
  hasGoogle: boolean;
  hasYelp: boolean;
  loading: boolean;
}
interface UseSearchContextType extends SearchServiceType {
  currentSearch: string;
  updateCurrentSearch: (value: string) => void
}

const SearchServiceContext = React.createContext<UseSearchContextType | undefined>({} as UseSearchContextType);

export const SearchServiceProvider: React.FC<{ children: ReactNode }> = ({ children }) => {
  const logger = getLogger();
  const [value, setValue] = useState<SearchServiceType>({ hasGoogle: false, hasYelp: false, loading: true });
  const [currentSearch, setCurrentSearch] = useState<string>('');
  const searchParams = useSearchParams();
  const pathname = usePathname();

  useEffect(() => {
    const getSearchService = async () => {
      try {
        let hasGoogle = await getFeatureFlagState(featureFlags.SEARCH_GOOGLE_SERVICE);
        let hasYelp = await getFeatureFlagState(featureFlags.SEARCH_YELP_SERVICE);
        let searchType = SearchService.YELP;
        let searchTypeLocal = localStorage.getItem('currentSearch');
        const providerId = searchParams.get("providerId");
        if (hasGoogle && hasYelp) {
          let hasDefaultGoogle = await getFeatureFlagState(featureFlags.GCM_DEFAULT_GOOGLE_SEARCH);
          let hasDetaultYelp = await getFeatureFlagState(featureFlags.GCM_DEFAULT_YELP_SEARCH);
          if (hasDefaultGoogle) {
            searchType = SearchService.GOOGLE;
          }
          if (hasDetaultYelp) {
            searchType = SearchService.YELP;
          }
        }
        if ((!hasYelp || !hasGoogle) && searchTypeLocal) {
          localStorage.removeItem('currentSearch');
          searchTypeLocal = null;
        }
        if (!hasYelp && hasGoogle) {
          searchType = SearchService.GOOGLE;
        }
        if (hasYelp && !hasGoogle) {
          searchType = SearchService.YELP;
        }
        if (providerId && pathname == '/switch-merchant') {
          if (providerId === SearchService.GOOGLE) {
            hasGoogle = true;
          }
          if (providerId === SearchService.YELP) {
            hasYelp = true;
          }
        }
        if (searchTypeLocal) {
          setCurrentSearch(JSON.parse(searchTypeLocal))
        } else {
          localStorage.setItem('currentSearch', JSON.stringify(providerId || searchType));
          setCurrentSearch(providerId || searchType);
        }
        setValue({
          hasGoogle,
          hasYelp,
          loading: false
        });
      } catch (error) {
        logger.error(error);
      }
    }
    getSearchService();
  }, []);

  const updateCurrentSearch = (value: string) => {
    localStorage.setItem('currentSearch', JSON.stringify(value));
    setCurrentSearch(value);
  }

  const valueConfig = useMemo(() => {
    return {
      ...value,
      currentSearch,
      updateCurrentSearch
    }
  }, [value, currentSearch]);

  return (
    <SearchServiceContext.Provider value={valueConfig}>
      {children}
    </SearchServiceContext.Provider>
  );
};

export const useSearchService = (): UseSearchContextType => {
  const context = useContext(SearchServiceContext);
  if (!context) {
    throw new Error("useSearchService must be used within a useSearchServiceProvider");
  }
  return context;
};